import React, { useEffect, useState } from 'react';
import { Share2 } from 'lucide-react';

// Construct an invitation from public fields only, never the current URL or vote state.
export function questionInvitation(event, origin) {
  const url = `${new URL(origin).origin}/api/share/questions/${encodeURIComponent(event.id)}`;
  return { title: 'PharmaConsensus', text: `I’d like your take: ${event.title}\nHow would you vote?`, url };
}

function Invitation({ event }) {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [status, setStatus] = useState('');
  const invitation = questionInvitation(event, window.location.origin);
  useEffect(() => {
    if (!open) { setImage(null); return; }
    const controller = new AbortController();
    let objectUrl;
    fetch(`${invitation.url}/image.png`, { credentials: 'omit', signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('Image unavailable');
        const blob = await response.blob();
        if (blob.type !== 'image/png' || blob.size > 5000000) throw new Error('Image unavailable');
        if (controller.signal.aborted) return;
        const file = new File([blob], 'pharmaconsensus-question.png', { type: 'image/png' });
        objectUrl = URL.createObjectURL(blob);
        setImage({ file, url: objectUrl });
      }).catch(error => { if (error.name !== 'AbortError') setStatus('Image unavailable. You can still share the question link.'); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, invitation.url]);
  const share = async (withImage = false) => {
    try {
      // No asynchronous preparation before this call: preserve the mobile tap gesture.
      await navigator.share({ ...invitation, ...(withImage && image ? { files: [image.file] } : {}) });
      setStatus('');
    } catch (error) {
      if (error.name !== 'AbortError') setStatus('Sharing unavailable here. Copy the link below.');
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(invitation.url); setStatus('Question link copied. Your answer is not included.'); }
    catch { setStatus('Select and copy the question link below.'); }
  };
  let canShareImage = false;
  try { canShareImage = Boolean(image && navigator.canShare?.({ files: [image.file] })); } catch { /* Link sharing remains available. */ }
  const control = 'min-h-[44px] rounded-xl border border-slate-600 px-3 py-3 text-sm text-center font-semibold hover:bg-slate-700';
  return <section className="mt-5 border-t border-slate-700 pt-4" aria-label="Invite others">
    <button type="button" onClick={() => { setStatus(''); setOpen(value => !value); }} aria-expanded={open} className="min-h-[44px] flex items-center gap-2 text-violet-300 font-semibold"><Share2 size={18} />Invite someone to vote</button>
    {open && <div className="space-y-3 mt-2">
      <p className="text-sm text-slate-300">Share the question. Your answer stays private.</p>
      {image && <img src={image.url} alt={`Invitation to vote: ${event.title}. Both options shown without your answer.`} className="w-full rounded-xl" />}
      <div className="grid grid-cols-2 gap-2">
        {typeof navigator.share === 'function' && <button type="button" className={`${control} col-span-2 bg-violet-600 border-violet-500`} onClick={() => share()}>Share invitation</button>}
        {canShareImage && <button type="button" className={control} onClick={() => share(true)}>Share image</button>}
        <button type="button" className={control} onClick={copy}>Copy link</button>
        {image && <a className={control} href={image.url} download="pharmaconsensus-question.png">Download image</a>}
        <a className={control} target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(invitation.url)}`}>LinkedIn</a>
      </div>
      <p className="text-xs text-slate-400">Sharing an image? Include the question link so people can swipe their answer.</p>
      <input aria-label="Question invitation link" className="w-full min-w-0 rounded-lg p-2 bg-slate-950 text-slate-300 text-xs" value={invitation.url} readOnly onFocus={e => e.target.select()} />
      <p role="status" className="text-sm text-slate-300">{status}</p>
    </div>}
  </section>;
}
export default function SocialShare({ event }) { return <Invitation key={event.id} event={event} />; }
